import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { getLeadAnalytics, type LeadAnalytics } from "@/lib/analytics.functions";
import { exportAgentSubscribers } from "@/lib/newsletter.functions";
import { FIRM } from "@/lib/firm";

export const Route = createFileRoute("/analytics")({
  head: () => ({
    meta: [
      { title: fitTitle("Lead analytics (internal) | Coastside Law") },
      {
        name: "description",
        content:
          fitDescription("Internal reporting view showing which pages, landing pages and search terms produce consultation enquiries and bookings."),
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: fitTitle("Lead analytics (internal) | Coastside Law") },
      {
        property: "og:description",
        content: fitDescription("Internal reporting on the pages and keywords producing consultation enquiries."),
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AnalyticsPage,
});

const RANGES = [
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 365, label: "Last 12 months" },
  { value: 0, label: "All time" },
] as const;

const field =
  "w-full rounded-sm border border-input bg-card px-3 py-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/20";

function Panel({
  title,
  hint,
  rows,
  total,
}: {
  title: string;
  hint: string;
  rows: { label: string; count: number }[];
  total: number;
}) {
  const max = rows.reduce((acc, row) => Math.max(acc, row.count), 0) || 1;
  return (
    <section className="rounded-sm border border-border bg-card p-5">
      <h2 className="text-base font-semibold tracking-tight text-foreground">{title}</h2>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No data captured yet for this period.</p>
      ) : (
        <ul className="mt-4 space-y-2.5">
          {rows.map((row) => (
            <li key={row.label}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate text-foreground" title={row.label}>
                  {row.label}
                </span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {row.count}
                  {total > 0 ? ` · ${Math.round((row.count / total) * 100)}%` : ""}
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full rounded-full bg-muted">
                <div
                  className="h-1.5 rounded-full bg-primary"
                  style={{ width: `${Math.max(4, (row.count / max) * 100)}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AnalyticsPage() {
  const [code, setCode] = useState("");
  const [days, setDays] = useState<number>(90);
  const [data, setData] = useState<LeadAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportNote, setExportNote] = useState<string | null>(null);

  async function exportList() {
    if (!code.trim()) {
      setExportNote("Enter the access code above first.");
      return;
    }
    setExporting(true);
    setExportNote(null);
    try {
      const result = await exportAgentSubscribers({ data: { code: code.trim() } });
      const blob = new Blob([result.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `coastside-agent-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      setExportNote(`${result.count} subscriber${result.count === 1 ? "" : "s"} exported.`);
    } catch (err) {
      setExportNote(err instanceof Error ? err.message : "Could not export the list.");
    } finally {
      setExporting(false);
    }
  }

  async function load(nextDays = days, nextCode = code) {
    if (!nextCode.trim()) {
      setError("Enter the access code to view reporting.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await getLeadAnalytics({
        data: { code: nextCode.trim(), days: nextDays as 30 | 90 | 365 | 0 },
      });
      setData(result);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : "Could not load reporting.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        Internal · not indexed
      </p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
        Lead analytics
      </h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        Which pages, landing pages and search terms are producing consultation enquiries, bookings
        and checklist downloads for {FIRM.name}. Counts only — no client names or contact details
        are shown here.
      </p>

      <form
        className="mt-8 flex flex-wrap items-end gap-3 rounded-sm border border-border bg-muted/40 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          void load();
        }}
      >
        <div className="min-w-[220px] flex-1">
          <label htmlFor="code" className="block text-sm font-medium text-foreground">
            Access code
          </label>
          <input
            id="code"
            type="password"
            className={`${field} mt-1.5`}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoComplete="off"
          />
        </div>
        <div>
          <label htmlFor="range" className="block text-sm font-medium text-foreground">
            Period
          </label>
          <select
            id="range"
            className={`${field} mt-1.5`}
            value={days}
            onChange={(event) => {
              const next = Number(event.target.value);
              setDays(next);
              if (data) void load(next);
            }}
          >
            {RANGES.map((range) => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex h-[42px] items-center justify-center rounded-sm bg-primary px-5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
        >
          {loading ? "Loading…" : "View report"}
        </button>
      </form>

      {error ? (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <section className="mt-6 rounded-sm border border-border bg-card p-5">
        <h2 className="text-base font-semibold tracking-tight text-foreground">
          Agent &amp; broker update list
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Download the current subscriber list as a CSV to import into your bulk-email tool. Uses
          the same access code above.
        </p>
        <button
          type="button"
          disabled={exporting}
          onClick={() => void exportList()}
          className="mt-4 inline-flex h-[38px] items-center justify-center rounded-sm border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-60"
        >
          {exporting ? "Preparing…" : "Download subscriber CSV"}
        </button>
        {exportNote ? <p className="mt-3 text-xs text-muted-foreground">{exportNote}</p> : null}
      </section>

      {data ? (
        <div className="mt-10 space-y-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-sm border border-border bg-card p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Total submissions
              </p>
              <p className="mt-1 text-3xl font-semibold tabular-nums text-foreground">
                {data.total}
              </p>
            </div>
            <div className="rounded-sm border border-border bg-card p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Top converting page
              </p>
              <p className="mt-1 truncate text-lg font-semibold text-foreground">
                {data.byPage[0]?.label ?? "—"}
              </p>
            </div>
            <div className="rounded-sm border border-border bg-card p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Top search term
              </p>
              <p className="mt-1 truncate text-lg font-semibold text-foreground">
                {data.byKeyword[0]?.label ?? "—"}
              </p>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Panel
              title="Pages driving submissions"
              hint="The page the form was actually submitted from."
              rows={data.byPage}
              total={data.total}
            />
            <Panel
              title="Landing pages"
              hint="The first page of the visit — what search or referral actually brought them in."
              rows={data.byLandingPage}
              total={data.total}
            />
            <Panel
              title="Search terms"
              hint="Keywords passed through from search or campaign links (utm_term, or the query in the referring URL)."
              rows={data.byKeyword}
              total={data.total}
            />
            <Panel
              title="Google search vs website"
              hint="Plain-language channel: Google search, Google Business Profile, AI assistant, social, referral or direct."
              rows={data.byChannel}
              total={data.total}
            />
            <Panel
              title="Sources and channels"
              hint="Where the visit came from: search engine, AI assistant, referral or direct."
              rows={data.bySource}
              total={data.total}
            />

            <Panel
              title="Form type"
              hint="Which capture point produced the lead."
              rows={data.byForm}
              total={data.total}
            />
            <Panel
              title="Enquiries by suburb page"
              hint="Which local suburb page produced the lead (submitted page, otherwise the landing page)."
              rows={data.bySuburb}
              total={data.total}
            />
            <Panel
              title="Matter type"
              hint="The matter, deal stage or relationship selected on the form."
              rows={data.byMatter}
              total={data.total}
            />
          </div>

          <section className="rounded-sm border border-border bg-card p-5">
            <h2 className="text-base font-semibold tracking-tight text-foreground">
              Google Business Profile clicks
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Visits arriving on tagged profile links (utm_campaign starting with{" "}
              <code className="font-mono">gbp</code>). Google strips the referrer on profile
              buttons, so these are the only reliable map-pack numbers.
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div className="rounded-sm border border-border bg-muted/40 p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Profile clicks
                </p>
                <p className="mt-1 text-3xl font-semibold tabular-nums text-foreground">
                  {data.gbp.total}
                </p>
              </div>
              <div className="rounded-sm border border-border bg-muted/40 p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Busiest profile
                </p>
                <p className="mt-1 truncate text-lg font-semibold text-foreground">
                  {data.gbp.byProfile[0]?.label ?? "—"}
                </p>
              </div>
              <div className="rounded-sm border border-border bg-muted/40 p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Most-clicked page
                </p>
                <p className="mt-1 truncate text-lg font-semibold text-foreground">
                  {data.gbp.byPage[0]?.label ?? "—"}
                </p>
              </div>
            </div>
          </section>

          <div className="grid gap-6 md:grid-cols-2">
            <Panel
              title="Clicks by page"
              hint="Which page each profile click actually landed on."
              rows={data.gbp.byPage}
              total={data.gbp.total}
            />
            <Panel
              title="Clicks by profile"
              hint="Golden Beach versus Scarborough listing."
              rows={data.gbp.byProfile}
              total={data.gbp.total}
            />
            <Panel
              title="Clicks by service link"
              hint="The utm_content tag on the profile button or service link (conveyancing, wills, probate, selling)."
              rows={data.gbp.byService}
              total={data.gbp.total}
            />
            <Panel
              title="Clicks by campaign tag"
              hint="Full utm_campaign value, including Google Posts links."
              rows={data.gbp.byCampaign}
              total={data.gbp.total}
            />
            <Panel
              title="Profile clicks by month"
              hint="Map-pack click trend across the period."
              rows={data.gbp.byMonth}
              total={data.gbp.total}
            />
            <Panel
              title="Enquiries credited to Google"
              hint="Landing pages of enquiries whose first touch was tagged utm_source=google — clicks that turned into a lead."
              rows={data.gbp.enquiries}
              total={data.total}
            />
          </div>

          <Panel
            title="Submissions by month"
            hint="Volume trend across the selected period."
            rows={data.byMonth}
            total={data.total}
          />

          <p className="text-xs leading-relaxed text-muted-foreground">
            Search terms only appear where a search engine or campaign link passes them through.
            Google hides most organic keywords, so pair this view with Search Console for full
            keyword coverage — landing pages here are the reliable proxy.
          </p>
        </div>
      ) : null}
    </div>
  );
}
